// Right-hand main column of the /mail page — the list pane + the thread view
// as flex siblings (architecture/MAIL.md §3.1 three-pane layout). With no
// working mailbox the §3.2a gate owns the pane instead (MailEmptyState mounts
// MailSetupPanel); the gate is the server's `state`, never an account count
// (the MailDrawerEmpty precedent).
export const MailStatusBanner = {
  subscribeTo: ['mailAccounts'],
  // One-line banner over the list: backfill progress, or the reconnect ask
  // for a `reauth_required` / `error` account (§3.1 states table).
  hide: (el, s) => {
    const accounts = (s.root && s.root.mailAccounts) || []
    return !accounts.some(
      (a) =>
        a &&
        (a.status === 'reauth_required' ||
          a.status === 'error' ||
          (a.sync && a.sync.status === 'backfill'))
    )
  },
  flow: 'x',
  align: 'center left',
  gap: 'Z',
  margin: 'Z Z1 0 Z1',
  padding: 'Z Z1',
  round: 'radiusControl',
  background: 'hover',
  flexShrink: '0',
  minWidth: '0',

  Note: {
    tag: 'span',
    fontSize: 'Y1',
    color: 'caption',
    flex: '1',
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    subscribeTo: ['mailAccounts'],
    text: (el, s) => {
      const accounts = (s.root && s.root.mailAccounts) || []
      const broken = accounts.find(
        (a) => a && (a.status === 'reauth_required' || a.status === 'error')
      )
      if (broken) {
        return el
          .call('polyglot', 'mail.banner.reconnect')
          .replace('{address}', broken.address || '')
      }
      const backfilling = accounts.find((a) => a && a.sync && a.sync.status === 'backfill')
      const progress = backfilling && backfilling.sync && backfilling.sync.progress
      return el
        .call('polyglot', 'mail.banner.backfill')
        .replace('{p}', progress != null ? `${Math.round(Number(progress))}%` : '…')
    }
  },

  ReconnectBtn: {
    subscribeTo: ['mailAccounts'],
    hide: (el, s) =>
      !((s.root && s.root.mailAccounts) || []).some(
        (a) => a && (a.status === 'reauth_required' || a.status === 'error')
      ),
    tag: 'button',
    type: 'button',
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    color: 'accentInk',
    fontSize: 'Y1',
    fontWeight: '700',
    padding: 'V Z',
    round: 'radiusControl',
    flexShrink: '0',
    ':hover': { background: 'hover' },
    text: '{{ mail.banner.reconnectAction | polyglot }}',
    onClick: (e, el, s) => {
      e.stopPropagation()
      const accounts = (s.root && s.root.mailAccounts) || []
      const broken = accounts.find(
        (a) => a && (a.status === 'reauth_required' || a.status === 'error')
      )
      if (broken) el.call('reconnectMailAccount', broken.id)
    }
  }
}

export const MailMain = {
  flow: 'x',
  flex: '1 1 0',
  minWidth: '0',
  minHeight: '0',
  overflow: 'hidden',
  theme: 'document',
  // Light takes the house content surface, so the pane matches the sidebar
  // card beside it — same reasoning as ChatMain (Rule 13).
  '@light': { background: 'white' },
  '@mobileL': { position: 'relative', flow: 'y' },

  // Not set up → the §3.2a gate (the shipped empty state). `ready` is the
  // ONLY state with a working mailbox (the MailDrawerEmpty precedent) — the
  // read panes and this poster swap on exactly that answer. FA515: the
  // `s.root &&` guard short-circuits, so each gate declares its dep.
  MailEmptyState: {
    hide: (el, s) => (s && s.root && s.root.mailSetup && s.root.mailSetup.state) === 'ready',
    hideDeps: [(el, s) => s.root && s.root.mailSetup]
  },

  ListPane: {
    hide: (el, s) => ((s.root && s.root.mailSetup && s.root.mailSetup.state) || '') !== 'ready',
    hideDeps: [(el, s) => s.root && s.root.mailSetup],
    flow: 'y',
    flex: '1 1 0',
    minWidth: '0',
    minHeight: '0',

    MailStatusBanner: {},
    MailThreadList: {}
  },

  MailThreadView: {
    hide: (el, s) => ((s.root && s.root.mailSetup && s.root.mailSetup.state) || '') !== 'ready',
    hideDeps: [(el, s) => s.root && s.root.mailSetup]
  }
}
